import { cn } from "@/lib/utils";
import { ActivityLogListItem } from "@/app/ActivityLogs/GraphQL/activityLogListQuery";
import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";
import dayjs from "dayjs";
import { MailIcon, MailWarningIcon } from "lucide-react";
import { EmailTimelineDialog } from "@/app/EmailEvents/Components/EmailTimelineDialog";
import { ModelType } from "@/gql/graphql";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from "use-intl";
import { getUserDisplayName } from "@/lib/Utils/getUserDisplayName";

export function EmailActivityLogItem({
  log,
  modelType,
  modelId,
  failed = false,
}: {
  log: ActivityLogListItem;
  modelType: ModelType;
  modelId: string;
  failed?: boolean;
}) {
  const { open: openEmailTimeline } = useDialog(EmailTimelineDialog);
  const t = useTranslations("activityLog.notation");

  const Icon = failed ? MailWarningIcon : MailIcon;

  return (
    <div
      className="flex cursor-pointer rounded-md bg-transparent transition-colors hover:bg-gray-50"
      onClick={() =>
        openEmailTimeline({
          objectType: modelType,
          objectId: modelId,
        })
      }
    >
      <div className="relative z-10 mr-4 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white">
        <Icon
          className={cn("size-5", failed ? "text-red-500" : "text-blue-400")}
          aria-hidden="true"
        />
      </div>

      <div className="min-w-0 grow text-xs text-gray-500">
        <div className="mr-1 flex shrink-0 items-center gap-1.5 font-medium text-gray-900">
          {getUserDisplayName(log.causer, t("guest"))}
          {failed ? (
            <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-600">
              {t("deliveryFailed")}
            </span>
          ) : null}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn("line-clamp-2", failed ? "text-red-600" : null)}>
              {log.description}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="max-w-sm">
            {log.description}
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="isolate flex w-[120px] shrink-0 justify-end text-gray-500">
        <time
          title={`${dayjs.tz.guess()}: ${formatDateTimeLocal(log.created_at)}`}
        >
          {formatDateTimeSinceNow(log.created_at)}
        </time>
      </div>
    </div>
  );
}
